import cx from "clsx"
import { describedBy } from "./describedBy"
import { FieldHint } from "./FieldHint"
import styles from "./TextArea.module.css"

type Props = {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  error?: string
  /** How to answer it, shown under the label and read with the control. */
  hint?: string
  // Error styling without a message of its own — for a field that is one part of
  // a group, where the message belongs to the group rather than to each part.
  invalid?: boolean
  required?: boolean
  name?: string
  rows?: number
  maxLength?: number
}

/**
 * A multi-line field. `InputField`'s sibling: the same label, the same four
 * states and the same accessible contract, over a textarea instead of an input.
 */
export function TextArea({
  id,
  label,
  value,
  onChange,
  placeholder,
  disabled,
  error,
  hint,
  invalid,
  required,
  name,
  rows = 3,
  maxLength,
}: Props) {
  return (
    <div className={cx(styles.field, { [styles.error]: error || invalid })}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required && (
          <span aria-hidden="true" className={styles.required}>
            {" "}
            *
          </span>
        )}
      </label>
      {hint && <FieldHint id={`${id}-hint`}>{hint}</FieldHint>}
      <textarea
        id={id}
        name={name ?? id}
        className={styles.textarea}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        maxLength={maxLength}
        required={required}
        aria-invalid={!!error || !!invalid}
        aria-describedby={describedBy(
          hint && `${id}-hint`,
          error && `${id}-error`,
        )}
      />
      {error && (
        <span
          id={`${id}-error`}
          className={styles.errorMessage}
          aria-live="polite"
        >
          {error}
        </span>
      )}
    </div>
  )
}
